/**
 * Servicio unificado de notificaciones para SICECOM ERP
 * Cumple con las especificaciones de SYSTEM.md sección 14
 */

import api from './api.js'

// Función para decodificar JWT
function decodificarJwt(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payload)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

class NotificationService {
  constructor() {
    this.isInitialized = false
    this.init()
  }

  init() {
    if (this.isInitialized) return

    // Esperar a que el componente esté disponible
    if (typeof window !== 'undefined') {
      this.waitForNotificationSystem()
    }

    this.isInitialized = true
  }

  waitForNotificationSystem() {
    const checkInterval = setInterval(() => {
      if (window.$notification) {
        clearInterval(checkInterval)
        this.notificationSystem = window.$notification
      }
    }, 100)

    // Timeout después de 10 segundos
    setTimeout(() => {
      clearInterval(checkInterval)
      if (!this.notificationSystem) {
        console.warn('[NotificationService] Sistema de notificaciones no disponible después de 10 segundos')
      }
    }, 10000)
  }

  /**
   * Muestra una notificación de éxito
   * @param {string} message - Mensaje a mostrar
   * @param {object} options - Opciones adicionales
   */
  async success(message, options = {}) {
    await this.logNotification('success', message)
    if (window.$notification) {
      return window.$notification.success(message, options)
    }
    console.log(`[SUCCESS] ${message}`)
  }

  /**
   * Muestra una notificación de error
   * @param {string} message - Mensaje a mostrar
   * @param {object} options - Opciones adicionales
   */
  async error(message, options = {}) {
    await this.logNotification('error', message)
    if (window.$notification) {
      return window.$notification.error(message, options)
    }
    console.error(`[ERROR] ${message}`)
  }

  /**
   * Muestra una notificación de advertencia
   * @param {string} message - Mensaje a mostrar
   * @param {object} options - Opciones adicionales
   */
  async warning(message, options = {}) {
    await this.logNotification('warning', message)
    if (window.$notification) {
      return window.$notification.warning(message, options)
    }
    console.warn(`[WARNING] ${message}`)
  }

  /**
   * Muestra una notificación informativa
   * @param {string} message - Mensaje a mostrar
   * @param {object} options - Opciones adicionales
   */
  async info(message, options = {}) {
    await this.logNotification('info', message)
    if (window.$notification) {
      return window.$notification.info(message, options)
    }
    console.info(`[INFO] ${message}`)
  }

  /**
   * Cierra una notificación específica
   * @param {number} id - ID de la notificación
   */
  close(id) {
    if (this.notificationSystem) {
      this.notificationSystem.close(id)
    }
  }

  /**
   * Limpia todas las notificaciones
   */
  clear() {
    if (this.notificationSystem) {
      this.notificationSystem.clear()
    }
  }

  /**
   * Registra la notificación en el log para auditoría
   * @param {string} type - Tipo de notificación
   * @param {string} message - Mensaje
   */
  async logNotification(type, message) {
    const timestamp = new Date().toISOString()
    const empresa = this.getCurrentEmpresa()
    const usuario = this.getCurrentUsuario()

    // Log para auditoría según SYSTEM.md
    console.log(`[AUDITORIA-NOTIFICACION] ${timestamp} | Empresa: ${empresa} | Usuario: ${usuario} | Tipo: ${type} | Mensaje: ${message}`)

    // Enviar al backend para registro en auditoría
    await this.sendToAuditLog(type, message, empresa, usuario)
  }

  /**
   * Obtiene la empresa actual del contexto
   */
  getCurrentEmpresa() {
    const token = localStorage.getItem('token_acceso_admin') || localStorage.getItem('access_token');
    if (!token) return 'desconocida';
    const payload = decodificarJwt(token);
    return payload?.empresa_id || 'desconocida';
  }

  /**
   * Obtiene el usuario actual
   */
  getCurrentUsuario() {
    const token = localStorage.getItem('token_acceso_admin') || localStorage.getItem('access_token');
    if (!token) return 'desconocido';
    const payload = decodificarJwt(token);
    return payload?.sub || 'desconocido';
  }

  /**
   * Envía registro de notificación al log de auditoría del backend
   * @param {string} type - Tipo de notificación
   * @param {string} message - Mensaje de la notificación
   * @param {string} empresa - ID de la empresa
   * @param {string} usuario - ID del usuario
   */
  async sendToAuditLog(type, message, empresa, usuario) {
    // No enviar si empresa o usuario son desconocidos (para admin sin empresa)
    if (empresa === 'desconocida' || usuario === 'desconocido') {
      console.log('[AUDITORIA] Omitiendo registro de notificación para admin sin empresa/usuario válido');
      return;
    }

    try {
      const auditData = {
        tipo: type,
        mensaje: message,
        usuario_id: usuario,
        timestamp: new Date().toISOString(),
        ip: await this.getClientIP(),
        modulo: 'notificaciones',
        user_agent: navigator.userAgent,
        url: window.location.href
      };

      // Solo incluir empresa_id si no es 'desconocida' (para admin)
      if (empresa !== 'desconocida') {
        auditData.empresa_id = empresa;
      }

      // Solo registrar en auditoría si hay empresa (client), no para admin
      if (empresa !== 'desconocida') {
        // Enviar al backend para registro en auditoría
        await api.post('/auditoria/notificaciones', auditData)
        console.log('[AUDITORIA] Notificación registrada exitosamente:', auditData)
      } else {
        console.log('[AUDITORIA] Omitiendo registro para admin sin empresa')
      }
    } catch (error) {
      console.error('[AUDITORIA] Error al registrar notificación:', error)
      // No lanzamos el error para no interrumpir el flujo de notificaciones
    }
  }

  /**
   * Obtiene la IP del cliente
   * @returns {Promise<string>} IP del cliente
   */
  async getClientIP() {
    try {
      // Intentar obtener IP desde un servicio externo
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip
    } catch (error) {
      console.warn('[AUDITORIA] No se pudo obtener IP del cliente:', error)
      return 'desconocida'
    }
  }
}

// Crear instancia singleton
const notificationService = new NotificationService()

export default notificationService

// Exportar funciones convenientes para uso directo
export const notifySuccess = (message, options) => notificationService.success(message, options)
export const notifyError = (message, options) => notificationService.error(message, options)
export const notifyWarning = (message, options) => notificationService.warning(message, options)
export const notifyInfo = (message, options) => notificationService.info(message, options)